import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, ShieldOff, Plus, Pencil, Trash2, Percent, PoundSterling, Tag, Calendar, Power, ArrowLeft } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { Link } from 'react-router-dom';
import DiscountCodeFormModal from '@/components/admin/DiscountCodeFormModal';
import { useAuth } from '@/lib/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import ConfirmDialog from '@/components/admin/ConfirmDialog';

export default function AdminDiscountCodes() {
  const { user, isLoadingAuth } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { data: codes = [], isLoading } = useQuery({
    queryKey: ['discountCodes'],
    queryFn: () => base44.entities.DiscountCode.list('-created_date', 200),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['discountCodes'] });

  const handleDelete = (code) => setDeleteTarget(code);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeletingId(deleteTarget.id);
    try {
      await base44.entities.DiscountCode.delete(deleteTarget.id);
      refresh();
      setDeleteTarget(null);
    } catch (err) {
      toast({ title: 'Could not delete', description: err?.message, variant: 'destructive' });
    }
    setDeletingId(null);
  };

  const toggleActive = async (code) => {
    try {
      await base44.entities.DiscountCode.update(code.id, { active: !code.active });
      refresh();
    } catch (err) {
      toast({ title: 'Could not update', description: err?.message, variant: 'destructive' });
    }
  };

  const openNew = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (code) => { setEditing(code); setModalOpen(true); };

  if (isLoadingAuth) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }
  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <div className="text-center">
          <ShieldOff className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm font-bold tracking-widest">ACCESS DENIED</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      <section className="py-10 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-white/40 hover:text-white text-xs font-bold tracking-wider mb-4 transition-colors">
              <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
            </Link>
            <p className="text-primary text-xs font-bold tracking-widest mb-2">PRODUCTS &amp; CATALOG</p>
            <h1 className="text-white font-black text-2xl sm:text-3xl uppercase">DISCOUNT CODES</h1>
            <p className="text-white/30 text-xs mt-2">Create and manage promotional discount codes for checkout.</p>
          </div>
          <button onClick={openNew}
            className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest px-5 py-3 transition-colors">
            <Plus className="w-4 h-4" /> NEW CODE
          </button>
        </div>
      </section>

      <section className="px-6 lg:px-10 py-10 max-w-5xl mx-auto">
        {isLoading ? (
          <div className="flex justify-center py-24">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : codes.length === 0 ? (
          <div className="text-center py-24 border border-dashed border-white/10">
            <Tag className="w-10 h-10 text-white/10 mx-auto mb-4" />
            <p className="text-white/40 text-sm font-bold tracking-widest mb-1">NO DISCOUNT CODES YET</p>
            <p className="text-white/20 text-xs mb-6">Create your first promotional code to get started.</p>
            <button onClick={openNew}
              className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest px-5 py-3 transition-colors">
              <Plus className="w-4 h-4" /> CREATE FIRST CODE
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {codes.map((code) => {
              const isPercent = code.type === 'percentage';
              const valueLabel = isPercent ? `${code.value}% off` : `£${Number(code.value).toFixed(2)} off`;
              const unlimited = !code.max_uses || code.max_uses === 0;
              const expired = code.expiry_date && new Date(code.expiry_date + 'T23:59:59') < new Date();
              const usageExceeded = code.max_uses && code.used_count >= code.max_uses;
              const live = code.active !== false && !expired && !usageExceeded;

              return (
                <div key={code.id}
                  className={`border p-5 transition-colors ${live ? 'border-white/10 bg-[#111115]' : 'border-white/5 bg-[#0d0d0f] opacity-60'}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <span className="text-primary font-mono font-black text-lg tracking-[0.1em]">{code.code}</span>
                        <span className={`text-[9px] font-bold tracking-widest px-2 py-1 border ${
                          live ? 'border-green-500/30 bg-green-500/10 text-green-400' : 'border-white/10 bg-white/5 text-white/40'
                        }`}>
                          {live ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                        {code.one_per_customer && (
                          <span className="text-[9px] font-bold tracking-widest px-2 py-1 border border-primary/30 bg-primary/10 text-primary">1 PER CUSTOMER</span>
                        )}
                      </div>

                      <div className="flex items-center gap-2 mb-3">
                        {isPercent
                          ? <Percent className="w-3.5 h-3.5 text-primary/60" />
                          : <PoundSterling className="w-3.5 h-3.5 text-primary/60" />}
                        <span className="text-white font-bold text-sm">{valueLabel}</span>
                        {code.min_spend > 0 && (
                          <span className="text-white/30 text-xs ml-2">Min spend £{Number(code.min_spend).toFixed(2)}</span>
                        )}
                      </div>

                      {code.description && (
                        <p className="text-white/30 text-xs mb-3">{code.description}</p>
                      )}

                      <div className="flex items-center gap-4 text-[11px] flex-wrap">
                        <span className="flex items-center gap-1.5 text-white/40">
                          <Tag className="w-3 h-3" />
                          {unlimited
                            ? `${code.used_count || 0} used · unlimited`
                            : `${code.used_count || 0} / ${code.max_uses} used`}
                        </span>
                        {code.expiry_date && (
                          <span className={`flex items-center gap-1.5 ${expired ? 'text-red-400' : 'text-white/40'}`}>
                            <Calendar className="w-3 h-3" />
                            {expired ? 'Expired ' : 'Expires '}{new Date(code.expiry_date).toLocaleDateString('en-GB')}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => toggleActive(code)}
                        title={code.active !== false ? 'Deactivate' : 'Activate'}
                        className="w-9 h-9 border border-white/10 hover:border-primary/40 text-white/40 hover:text-primary flex items-center justify-center transition-colors">
                        <Power className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => openEdit(code)}
                        className="w-9 h-9 border border-white/10 hover:border-primary/40 text-white/40 hover:text-primary flex items-center justify-center transition-colors">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => handleDelete(code)}
                        disabled={deletingId === code.id}
                        className="w-9 h-9 border border-white/10 hover:border-red-500/40 text-white/40 hover:text-red-400 flex items-center justify-center transition-colors disabled:opacity-40">
                        {deletingId === code.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete discount code"
        description={deleteTarget ? `Delete code "${deleteTarget.code}" permanently? This cannot be undone.` : ''}
        confirmLabel="Delete"
        onConfirm={confirmDelete}
        onClose={() => setDeleteTarget(null)}
      />

      <DiscountCodeFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        editing={editing}
        onSaved={refresh}
      />
    </div>
  );
}